import { Email as EmailIcon } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { HttpStatusCode } from 'axios'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { axios } from '~/axios'
import { sendUsernamesByEmail } from '~/features/Auth/services/password-recovery.service'

interface ForgotUsernameDialogProps {
  isOpen: boolean
  onClose: () => void
}

export default function ForgotUsernameDialog({
  isOpen,
  onClose,
}: ForgotUsernameDialogProps) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const [email, setEmail] = useState('')

  const mutation = useMutation({
    mutationFn: sendUsernamesByEmail,
    onSuccess: () => {
      enqueueSnackbar(t('views.login.forgotUsernameSuccess'), {
        variant: 'success',
      })
      handleClose()
    },
    onError: error => {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === HttpStatusCode.NotFound
      ) {
        enqueueSnackbar(t('views.login.forgotUsernameNotFound'), {
          variant: 'error',
        })
        return
      }

      enqueueSnackbar(t('general.error'), {
        variant: 'error',
      })
    },
  })

  function handleClose() {
    setEmail('')
    onClose()
  }

  function handleSubmit() {
    mutation.mutate(email)
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('views.login.forgotUsernameTitle')}</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('views.login.forgotUsernameInfo')}
        </Typography>

        <TextField
          fullWidth
          autoFocus
          type="email"
          value={email}
          label={t('views.login.email')}
          onChange={e => {
            setEmail(e.target.value)
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && email) handleSubmit()
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isLoading}>
          {t('general.cancel')}
        </Button>
        <Button
          variant="contained"
          endIcon={<EmailIcon />}
          disabled={!email}
          loading={mutation.isLoading}
          onClick={handleSubmit}
        >
          {t('general.send')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
